/** Combines-with option keys used by ShopifyCouponTermsCards' checkbox row
 *  AND by the campaign config form's / coupon edit modal's summary sidebars.
 *  Exported from a plain module (not a component file) so a label change in
 *  one place flows through every surface without tripping
 *  react-refresh/only-export-components. Keys are the FE-camelCase short
 *  form; `configField` maps each to the wire-contract snake_case
 *  CampaignShopifyConfigInput field name. */
export const COMBINES_WITH_OPTIONS: ReadonlyArray<{
  key: 'orderDiscounts' | 'productDiscounts' | 'shippingDiscounts';
  configField:
    | 'combines_with_order_discounts'
    | 'combines_with_product_discounts'
    | 'combines_with_shipping_discounts';
  label: string;
  /** Mirrors the sub-label Shopify's own "Create discount" screen shows under
   *  each combinesWith checkbox — how many of that class can stack per order. */
  hint: string;
}> = [
  {
    key: 'productDiscounts',
    configField: 'combines_with_product_discounts',
    label: 'Combines with product discounts',
    hint: 'Multiple can apply per order',
  },
  {
    key: 'orderDiscounts',
    configField: 'combines_with_order_discounts',
    label: 'Combines with order discounts',
    hint: 'Multiple can apply per order',
  },
  {
    key: 'shippingDiscounts',
    configField: 'combines_with_shipping_discounts',
    label: 'Combines with shipping discounts',
    hint: 'Only one can apply per order (best value wins)',
  },
];

export const STACKED_CODES_NOTE =
  'If an order uses more than one Discovr code, one creator gets the credit: the first eligible Discovr code, in the order Shopify lists the codes.';

/** The Combinations card's own summary sentence — verbatim Shopify wording
 *  for the "nothing selected" state ("This discount won't combine with
 *  other product, order, or shipping discounts in the customer's cart."),
 *  extrapolated the same way for one/two/three selected. Distinct from
 *  combinesWithSummary (the comma-joined SUMMARY-sidebar row) — this is the
 *  sentence shown next to the "Edit combinations" popover trigger. */
export function combinesWithSentence(order: boolean, product: boolean, shipping: boolean): string {
  const values: Record<(typeof COMBINES_WITH_OPTIONS)[number]['key'], boolean> = {
    orderDiscounts: order,
    productDiscounts: product,
    shippingDiscounts: shipping,
  };
  const active = COMBINES_WITH_OPTIONS.filter((opt) => values[opt.key]).map((opt) =>
    opt.key === 'shippingDiscounts'
      ? 'shipping'
      : opt.key === 'orderDiscounts'
        ? 'order'
        : 'product',
  );
  if (active.length === 0) {
    return "This discount won't combine with other product, order, or shipping discounts in the customer's cart.";
  }
  const joined =
    active.length === 1
      ? active[0]
      : active.length === 2
        ? `${active[0]} and ${active[1]}`
        : `${active.slice(0, -1).join(', ')}, and ${active[active.length - 1]}`;
  return `This discount combines with ${joined} discounts.`;
}
